import HighchartsReact from "highcharts-react-official";
import Highcharts from "highcharts";
import React, { useEffect, useState } from "react";
import { DbUserService } from "app/services/db/dbUserService";
import moment from "moment";
import { Constant } from "app/constants/constant";
import { ResponseResultCode } from "app/model/response-api";
import { ChartUtil } from "app/utils/chartUtil";
import {numberFormatter, parseNumberWithCommaDecimal, round, roundWithComma} from "app/utils/stringUtil";
import CountUp from "react-countup";
import {CountUpUtil} from "app/utils/countUpUtil";
import {useAppSelector} from "app/store/hook";
import DashboardLoading from "../modules/DashboardLoading";
import {DashboardModuleProps} from "app/model/db/dashboardModuleProps";
import DashboardCard from "../modules/DashboardCard";
import {HighchartsOptionsUtil} from "app/utils/highchartsOptionsUtil";

const day = (moment().month() + 1).toString();
const thisMonthDefaultSeries: any = {
  borderWidth: 0,
  type: 'areaspline',
  name: '금월',
  data: ChartUtil.dayOfMonthArray(day).map(()=>0),
  color: '#8FE4D2',
  marker: {
    enabled: false,
    symbol: 'circle',
    lineColor: 'rgba(43,215,246,1)',
    fillColor: 'rgba(43,215,246,1)'
  },
  fillColor: {
    linearGradient: {
      x1: 0,
      y1: 0,
      x2: 0,
      y2: 1
    },
    stops: [
      [-0.1923, '#8EE3D2'], // CSS의 시작점과 색상
      [0.9787, 'rgba(125, 192, 235, 0)']
    ]
  },
};

const lastMonthDefaultSeries: any = {
  type: 'areaspline',
  name: '전월',
  data: ChartUtil.dayOfMonthArray(day).map(()=>0),
  color: '#999',
  marker: {
    enabled: false,
    symbol: 'circle',
    lineColor: '#999',
    fillColor: '#999'
  },
  fillColor: {
    linearGradient: {
      x1: 0,
      y1: 0,
      x2: 0,
      y2: 1
    },
    stops: [
      [0, '#737373'],
      [0.815, 'rgba(217, 217, 217, 0)']
    ]
  },
};

const defaultOptions: Highcharts.Options = {
  chart: {
    type: 'column',
    height: 277,
    spacingTop: 0,
    spacingBottom: 0,
  },
  title: {
    text: ''
  },
  exporting: {
    enabled: false
  },
  credits: {
    enabled: false
  },
  noData: HighchartsOptionsUtil.noData,
  tooltip: {
    headerFormat: '<b>{point.x}</b><br/>',
    style: {
      fontSize: '0.75rem',
    },
    formatter: function (this: Highcharts.TooltipFormatterContextObject) {
      let tooltip = `<span><strong style="font-size: 0.75rem;">${this.x}</strong></span><br/>`;
      this.points?.forEach((point: any) => {
        tooltip += `<span style="color:${point.color}">●&nbsp;</span>${point.series.name}: <strong style="font-size: 0.75rem;">${numberFormatter(point.y,2)}</strong> kWh<br/>`;
      });
      return tooltip;
    },
  },
  legend: {
    align: 'right',
    verticalAlign: 'top',
    layout: 'horizontal',
    symbolHeight: 10,
    itemStyle: {
      fontSize: '0.75rem',
      color: '#444444',
    },
  },
  xAxis: {
    categories: ChartUtil.daysOfMonth(),
    min: 0,
    crosshair: true,
    gridLineColor: '#CCC',
    gridLineWidth: 1,
    lineColor: '#CCC',
    lineWidth: 1,
    labels: {
      style: {
        color: '#444',
        fontSize: '0.7rem'
      },
    }
  },
  yAxis: {
    title: {
      text: '(kWh)',
      align: 'high',
      rotation: 0,
      offset: 0,
      y: -25,
      style: {
        color: '#444',
        fontSize: '0.65rem',
      },
    },
    min: 0,
    softMax: 10,
    gridLineColor: '#CCC',
    gridLineWidth: 1,
    labels: {
      x: -5,
      y: 4,
      style: {
        color: '#444',
        fontSize: '0.7rem',
      },
      formatter: function (this: any) {
        return parseNumberWithCommaDecimal(this.value);
      },
    },
  },
  series: [thisMonthDefaultSeries, lastMonthDefaultSeries],
  responsive: {
    rules: [{
      condition: {
        maxWidth: 350
      },
      chartOptions: {
        chart: {
          spacingTop: 20,
        },
        xAxis: {
          labels: {
            style: {
              fontSize: '0.5rem',
            },
          },
        },
        yAxis: [
          {
            title: {
              y: -10,
              style: {
                fontSize: '0.5rem',
              },
            },
            labels: {
              style: {
                fontSize: '0.5rem',
              },
            },
          },
        ],
        legend: {
          align: 'right',
          verticalAlign: 'top',
          layout: 'horizontal',
          itemStyle: {
            fontSize: '0.6rem',
          },
        },
        tooltip: {
          style: {
            fontSize: '0.6rem',
          }
        },
      }
    }]
  }
}

const today = moment().format(Constant.FORMAT_YMD_ALT);
const thisMonthString = moment().format('M');
const thisMonth = moment().format('YYYYMM');
const lastMonth = moment().subtract(1, 'month').format('YYYYMM');

export default function DbUserDayPowerGen(props: Readonly<DashboardModuleProps>) {
  const userInfo = useAppSelector((state) => state.userInfo.userInfo);
  const [options, setOptions] = useState<any>();
  const [series, setSeries] = useState<any[]>([thisMonthDefaultSeries, lastMonthDefaultSeries]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [summary, setSummary] = useState<any>();
  const [pwrSplyTime , setPwrSplyTime] = useState<any>();
  const pvName = userInfo.isDcUser ? "DC그리드 공급" : '발전'

  useEffect(() => {
    if(userInfo.isDcUser){
      DbUserService.getInstance().fetchPvMonthDataTime().then((res:any)=>{
        setPwrSplyTime(res.data.pwrSplyTime)
      })
    }

    DbUserService.getInstance().fetchPvMonthData().then(response => {
      if (response && response.resultCode === ResponseResultCode.Y && response.data) {
        const summary = response.data.summary;
        setSummary(summary);

        if(!userInfo.isDcUser){
          setPwrSplyTime(summary.pwrGenTime)
        }

        const data = response.data.data;
        const thisMonthList = data?.filter((d: any) => d.crtrYmd.substring(0, 6) === thisMonth);
        const lastMonthList = data?.filter((d: any) => d.crtrYmd.substring(0, 6) === lastMonth);
        const category = ChartUtil.getDayOfMonthCategories(today);
        const thisMonthData = ChartUtil.correctMissingDataByCategory2('DAY', thisMonthList, 'crtrYmd', 'pwrGen', category);
        const lastMonthData = ChartUtil.correctMissingDataByCategory2('DAY', lastMonthList, 'crtrYmd', 'pwrGen', category);
        const thisMonthSeries = thisMonthDefaultSeries;
        const lastMonthSeries = lastMonthDefaultSeries;
        thisMonthSeries.data = thisMonthData;
        lastMonthSeries.data = lastMonthData;
        if(thisMonthSeries.data.length == 0 && lastMonthSeries.data.length == 0){
          setSeries([{name:"금월", y:0},{name:"전월", y:0}])
        } else{
          setSeries([thisMonthSeries, lastMonthSeries]);
        }

      }
    }).finally(() => {
      setIsLoading(false);
    });
  }, []);

  useEffect(() => {
    const handleResize = () => {
      setOptions({...defaultOptions, series: series});
    };

    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  useEffect(() => {
    setOptions({
      ...defaultOptions,
      series: series,
    })
  }, [series]);

  return (
    <DashboardCard
      title={`월 ${pvName}량`}
      isConfigMode={props.isConfigMode ?? false}
      moreButtonVisible={true}
      moreButtonUrl={`${userInfo.isDcUser ? '/pc/eu/dcgridsply' : '/pc/renewal/solar'}`}
      dashboardConfig={props.dashboardConfig}
    >
      <DashboardLoading isLoading={isLoading} />
      {/*{!isLoading && !hasPv ? (*/}
      {/*  <DashboardNoData message="등록된 태양광 자원이 없습니다." />*/}
      {/*) : (*/}
        <div className="dash-box-flex">
          <div className="chart">
            {options && (
              <HighchartsReact highcharts={Highcharts} options={options}/>
            )}
          </div>
          <div className="flex-col2">
            <div className="box-style11 pd0">
              <div className="box-in">
                <span>{thisMonthString}월 {pvName}량</span>
                <span>
                  <strong>
                    {summary ? (
                      <CountUp
                        end={summary.pwrGen ?? 0}
                        decimals={CountUpUtil.getDecimal(round(summary.pwrGen ?? 0, 2))}
                        duration={0.5}
                      />
                    ) : (
                      <>-</>
                    )}
                  </strong>
                  <em>kWh</em>
                </span>
              </div>
            </div>
            <div className="list2">
              <span>{thisMonthString}월 평균 {pvName.includes("공급") ? "공급" : pvName}시간</span>
              <span>
                <strong>{pwrSplyTime ?? '-'}</strong>
                <em>시간</em>
              </span>
            </div>
            <div className="list2">
              <span>전월 {pvName.includes("공급") ? "공급" : pvName}량</span>
              <span>
                <strong>{summary ? roundWithComma(summary.pwrGenPrev ?? 0, 2) : '-'}</strong>
                <em>kWh</em>
              </span>
            </div>
          </div>
        </div>
      {/*)}*/}
    </DashboardCard>
  );
}